import SectionDivider from 'components/shared/SectionDivider';
import Typography from 'components/Typography';
import { FormattedMessage } from 'react-intl';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import PlanTypeType from 'types/PlanTypeType';
import ProResponse from 'types/ProResponse';
import { validatePaymentForm } from 'utils/PaymentFormHelper';

import CreditCardForm from 'components/forms/CreditCardForm';
import ApplePayButton from './ApplePayButton';
import ErrorText from './ErrorText';
import * as styles from './PaymentForm.module.scss';
import PayPalButton from './PayPalButton';

type Props = {
  coupon: Coupon;
  currencyCode: CurrencyCode;
  cvv: string;
  cvvError: React.ReactNode;
  errors: ProResponse['errors'];
  expiration: string;
  expirationError: React.ReactNode;
  name: string;
  nameError: React.ReactNode;
  number: string;
  numberError: React.ReactNode;
  plan: PlanTypeType;
  postalCode: string;
  setName: (name: string) => void;
  setNumber: (number: string) => void;
  setPostalCode: (postalCode: string) => void;
  userId: number;
  setCvv: (cvv: string) => void;
  setNumberError: (error: React.ReactNode) => void;
  setExpiration: (expiration: string) => void;
  setNameError: (error: React.ReactNode) => void;
  postalCodeError: React.ReactNode;
  setPostalCodeError: (error: React.ReactNode) => void;
  setErrors: (errors: ProResponse['errors']) => void;
  setCvvError: (error: React.ReactNode) => void;
  recurlyPublicKey: string;
  setExpirationError: (error: React.ReactNode) => void;
};

export default function PaymentForm({
  coupon,
  currencyCode,
  plan,
  recurlyPublicKey,
  userId,
  errors,
  setErrors,
  expiration,
  number,
  cvv,
  postalCode,
  name,
  setExpiration,
  setNumber,
  setCvv,
  setPostalCode,
  setName,
  setNumberError,
  numberError,
  nameError,
  setNameError,
  setCvvError,
  cvvError,
  postalCodeError,
  setPostalCodeError,
  expirationError,
  setExpirationError
}: Props) {
  function handleCreditCardFormChange(event: any) {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, {});

    switch (name) {
      case 'number':
        setNumber(value);
        break;
      case 'expiration':
        setExpiration(value);
        break;
      case 'cvv':
        setCvv(value);
        setCvvError(payload.errors.cvv);
        break;
      case 'postalCode':
        setPostalCode(value);
        setPostalCodeError(payload.errors.postalCode);
        break;
      case 'name':
        setName(value);
        setNameError(payload.errors.name);
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  }

  return (
    <>
      <ApplePayButton
        coupon={coupon}
        currencyCode={currencyCode}
        plan={plan}
        recurlyPublicKey={recurlyPublicKey}
        setErrors={setErrors}
        userId={userId}
      />
      <PayPalButton
        coupon={coupon}
        currencyCode={currencyCode}
        plan={plan}
        recurlyPublicKey={recurlyPublicKey}
        setErrors={setErrors}
        userId={userId}
      />
      <SectionDivider classes={styles.divider}>
        <Typography variant="caption">
          <FormattedMessage defaultMessage="Or enter payment details" />
        </Typography>
      </SectionDivider>
      <CreditCardForm
        changeHandler={handleCreditCardFormChange}
        cvv={cvv}
        errors={{ expiration: expirationError, name: nameError, number: numberError, cvv: cvvError, postalCode: postalCodeError }}
        expiration={expiration}
        isCouponInputExpanded={false}
        isCouponInputShown={false}
        name={name}
        number={number}
        postalCode={postalCode}
        setError={(name, error) => {
          // these are then only two errors set on the credit card form
          switch (name) {
            case 'expiration':
              setExpirationError(error);
              break;
            case 'number':
              setNumberError(error);
              break;
            default:
              throw new Error(`missing credit card form error handling for name '${name}'`);
          }
        }}
        // toggleCouponCode={null}
      />
      {errors && <ErrorText errors={errors} />}
    </>
  );
}
